import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtEUR } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, Building2, User, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const empty = { type: "particulier", name: "", contact_name: "", email: "", phone: "", address: "", postal_city: "", siret: "", tva_number: "", notes: "" };

export default function Clients() {
  const qc = useQueryClient();
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: api.listClients });
  const { data: invoices = [] } = useQuery({ queryKey: ["invoices"], queryFn: api.listInvoices });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["clients"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  useEffect(() => { if (open) setForm(editing ? { ...editing } : empty); }, [open, editing]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const spent = (id) => invoices.filter((i) => i.client_id === id && i.status === "payee").reduce((s, i) => s + (i.total_ttc || 0), 0);

  const save = async () => {
    if (!form.name) return toast.error("Le nom est requis");
    try {
      if (editing) await api.updateClient(editing.id, form);
      else await api.createClient(form);
      setOpen(false);
      refresh();
      toast.success(editing ? "Client modifié" : "Client ajouté");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement du client");
    }
  };

  const confirmDelete = async () => {
    await api.deleteClient(toDelete.id);
    setToDelete(null);
    refresh();
    toast.success("Client supprimé");
  };

  return (
    <div>
      <PageHeader
        title="Clients"
        subtitle={`${clients.length} client(s)`}
        action={
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="create-client-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Ajouter un client
          </Button>
        }
      />

      {clients.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`client-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
                    {c.type === "societe" ? <Building2 className="h-5 w-5 text-[#C5A059]" /> : <User className="h-5 w-5 text-[#C5A059]" />}
                  </div>
                  <div>
                    <p className="font-display font-semibold text-slate-900 leading-tight">{c.name}</p>
                    <p className="text-xs text-slate-400 capitalize">{c.type}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-client-${c.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setToDelete(c)} data-testid={`delete-client-${c.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-sm text-slate-600">
                {c.email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" />{c.email}</div>}
                {c.phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{c.phone}</div>}
                {c.address && <div className="text-slate-500 text-xs pl-5">{c.address}, {c.postal_city}</div>}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-400 uppercase tracking-wider">Total encaissé</span>
                <span className="font-mono font-semibold text-slate-900">{fmtEUR(spent(c.id))}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center text-slate-400"><Users className="h-8 w-8 mx-auto mb-2 opacity-40" />Aucun client</div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col gap-0 p-0" data-testid="client-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
            <DialogTitle className="font-display text-2xl">{editing ? "Modifier le client" : "Nouveau client"}</DialogTitle>
            <DialogDescription>Renseignez les coordonnées du client. Les champs marqués selon le type (société) apparaissent automatiquement.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 px-6 py-5 overflow-y-auto flex-1">
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="client-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="particulier">Particulier</SelectItem>
                  <SelectItem value="societe">Société</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Nom {form.type === "societe" ? "société" : "complet"}</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="client-name" /></div>
            {form.type === "societe" && <div className="space-y-1.5"><Label>Contact</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="client-contact" /></div>}
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="client-email" /></div>
            <div className="space-y-1.5"><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="client-phone" /></div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="client-address" /></div>
            <div className="space-y-1.5"><Label>Code postal + Ville</Label><Input value={form.postal_city} onChange={(e) => set("postal_city", e.target.value)} data-testid="client-city" /></div>
            {form.type === "societe" && <>
              <div className="space-y-1.5"><Label>SIRET</Label><Input value={form.siret} onChange={(e) => set("siret", e.target.value)} data-testid="client-siret" /></div>
              <div className="space-y-1.5"><Label>N° TVA intracom.</Label><Input value={form.tva_number} onChange={(e) => set("tva_number", e.target.value)} data-testid="client-tva" /></div>
            </>}
            <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="client-notes" /></div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-client-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le client ?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} sera supprimé. Les factures existantes sont conservées.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-client">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
